import { useEffect, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { STRIPE_PUBLISHABLE_KEY } from '../utils/env';
import { ENDPOINTS } from '../config';
import { supabase } from '../lib/supabase';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

interface CardSetupProps {
  onComplete?: (setupIntentId: string) => void;
  onError?: (message: string) => void;
  // internal: when provided, use this id to persist the card
  setupIntentIdOverride?: string;
}

function CardSetupInner({ onComplete, onError }: CardSetupProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setIsProcessing(true);
    try {
      const result = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      });

      if (result.error) {
        throw new Error(result.error.message || 'Failed to verify card');
      }
      const si = result.setupIntent;
      if (!si || si.status !== 'succeeded') {
        throw new Error(`Card verification incomplete (status: ${si?.status || 'unknown'})`);
      }
      const siId = si.id;
      // Persist card details to DB via update endpoint
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;
        if (!token) throw new Error('Not authenticated');
        await fetch(ENDPOINTS.updateCardSetupIntent, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ setupIntentId: siId }),
        });
      } catch {
        // Non-fatal: still consider setup success
      }
      onComplete?.(siId);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to save card';
      onError?.(msg);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-3">
      <PaymentElement options={{ layout: 'tabs' }} />
      <button
        className="w-full py-3 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
        disabled={!stripe || !elements || isProcessing}
        onClick={handleSubmit}
      >
        {isProcessing ? 'Saving Card…' : 'Save Card'}
      </button>
    </div>
  );
}

export function CardSetup(props: CardSetupProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  // The setup intent id is no longer used directly; omit storing it to avoid unused vars
  const setSetupIntentId = useState<string | null>(null)[1];
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;
        if (!token) throw new Error('Not authenticated');
        const res = await fetch(ENDPOINTS.createCardSetupIntent, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.error || 'Failed to start card setup');
        }
        const { clientSecret, setupIntentId } = await res.json();
        setClientSecret(clientSecret);
        setSetupIntentId(setupIntentId);
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Failed to initialize card setup';
        setError(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="text-sm text-gray-600">Preparing secure card setup…</div>;
  }
  if (error || !clientSecret) {
    return <div className="text-sm text-red-600">{error || 'Unable to initialize card setup'}</div>;
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <CardSetupInner {...props} />
    </Elements>
  );
}


